/**
 * Geo / Mapa — nodes, operators, caller IPs, country policy, customer networks.
 * Map is the shared GeoMap widget (OSM) — same as My Panel / dashboard.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { api } from '../../lib/api';
import { GeoMap } from '../../components/geo/GeoMap';
import {
  Ban,
  Building2,
  Crosshair,
  Globe,
  Map as MapIcon,
  MapPin,
  Navigation,
  Network,
  Plus,
  RefreshCw,
  Server,
  Trash2,
  User,
  Wifi,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Modal } from '../../components/ui/Modal';
import { StatCard, StatsGrid } from '../../components/ui/Stats';
import { Tab, TabList, TabPanel, Tabs } from '../../components/ui/Tabs';
import { Select } from '../../components/ui/Select';
import { Toggle } from '../../components/ui/Toggle';
import { themedConfirm } from '../../lib/themed-dialogs';

// ── Types ────────────────────────────────────────────────────────────────────

interface GeoPosition {
  id: string;
  kind: string;
  entity_id: string;
  label: string | null;
  lat: number;
  lon: number;
  source: string;
  user_id: string | null;
  accuracy: number | null;
  updated_at: string;
}

interface GeoCaller {
  id: string;
  ip: string;
  is_private: boolean;
  country_code: string | null;
  country_name: string | null;
  city: string | null;
  lat: number | null;
  lon: number | null;
  org: string | null;
  source: string;
  last_username: string | null;
  last_user_id: string | null;
  assigned_user_id: string | null;
  assigned_label: string | null;
  notes: string | null;
  hit_count: number;
  first_seen: string;
  last_seen: string;
  country_policy?: string | null;
  customer_id?: string | null;
  customer_label?: string | null;
  network_cidr?: string | null;
}

interface CountryPolicy {
  country_code: string;
  list_type: 'block' | 'allow';
  reason: string | null;
}

interface GeoNetwork {
  id: string;
  cidr: string;
  label: string | null;
  customer_id: string | null;
  customer_label: string | null;
  kind: string;
  is_private: boolean;
  color: string | null;
  lat: number | null;
  lon: number | null;
  notes: string | null;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

async function callMcp<T = unknown>(name: string, args: Record<string, unknown> = {}): Promise<T> {
  return api.mcp<T>(name, args);
}

/** DB/json někdy vrátí lat/lon jako string — toFixed musí dostat number. */
function fmtCoord(v: unknown, digits = 5): string {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n.toFixed(digits) : '—';
}

// ── Map panel = shared widget ────────────────────────────────────────────────

function MapPanel() {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader>
        <CardTitle icon={MapIcon}>{t('geo.mapTitle')}</CardTitle>
      </CardHeader>
      <CardContent>
        <GeoMap format='lg' showControls />
      </CardContent>
    </Card>
  );
}

// ── Positions panel ──────────────────────────────────────────────────────────

function PositionsPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    kind: 'node',
    entity_id: '',
    label: '',
    lat: '',
    lon: '',
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['geo-positions'],
    queryFn: () =>
      callMcp<{ ok: boolean; positions: GeoPosition[] }>('geo.position_list', { limit: 500 }),
    refetchInterval: 30_000,
  });
  const positions = data?.positions ?? [];

  const addMut = useMutation({
    mutationFn: async () => {
      const lat = Number(form.lat);
      const lon = Number(form.lon);
      if (!form.entity_id.trim()) throw new Error('entity_id required');
      if (!Number.isFinite(lat) || lat < -90 || lat > 90) throw new Error('invalid lat');
      if (!Number.isFinite(lon) || lon < -180 || lon > 180) throw new Error('invalid lon');
      return callMcp('geo.position_set', {
        kind: form.kind,
        entity_id: form.entity_id.trim(),
        label: form.label.trim() || undefined,
        lat,
        lon,
        source: 'manual',
      });
    },
    onSuccess: () => {
      toast.success(t('geo.positionSaved'));
      setAdding(false);
      setForm({ kind: 'node', entity_id: '', label: '', lat: '', lon: '' });
      qc.invalidateQueries({ queryKey: ['geo-positions'] });
      qc.invalidateQueries({ queryKey: ['geo-map'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const delMut = useMutation({
    mutationFn: (p: GeoPosition) =>
      callMcp('geo.position_delete', { kind: p.kind, entity_id: p.entity_id }),
    onSuccess: () => {
      toast.success(t('geo.positionDeleted'));
      qc.invalidateQueries({ queryKey: ['geo-positions'] });
      qc.invalidateQueries({ queryKey: ['geo-map'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card>
      <CardHeader>
        <div className='flex items-center justify-between w-full'>
          <CardTitle icon={MapPin}>
            {t('geo.positionsTitle')} ({positions.length})
          </CardTitle>
          <div className='flex gap-2'>
            <Button variant='secondary' size='sm' icon={<RefreshCw size={12} />} onClick={() => refetch()}>
              {t('geo.refresh')}
            </Button>
            <Button variant='primary' size='sm' icon={<Plus size={12} />} onClick={() => setAdding(true)}>
              {t('geo.addPosition')}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading
          ? <div className='text-xs text-gray-600 py-4'>{t('common.loading')}</div>
          : positions.length === 0
          ? <div className='text-xs text-gray-600 py-4 text-center'>{t('geo.noPositions')}</div>
          : (
            <div className='overflow-x-auto max-h-96 overflow-y-auto'>
              <table className='w-full text-xs'>
                <thead className='sticky top-0 bg-black'>
                  <tr className='text-gray-500 border-b border-gray-800'>
                    <th className='text-left py-1 px-2'>{t('geo.colKind')}</th>
                    <th className='text-left py-1 px-2'>{t('geo.colEntity')}</th>
                    <th className='text-left py-1 px-2'>{t('geo.colLabel')}</th>
                    <th className='text-left py-1 px-2'>Lat / Lon</th>
                    <th className='text-left py-1 px-2'>{t('geo.colSource')}</th>
                    <th className='text-right py-1 px-2'>{t('geo.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {positions.map((p) => (
                    <tr key={p.id} className='border-b border-gray-900 hover:bg-gray-900/40'>
                      <td className='py-1 px-2'>
                        <Badge variant={p.kind === 'traccar_device' ? 'info' : 'default'}>
                          {p.kind}
                        </Badge>
                      </td>
                      <td className='py-1 px-2 font-mono text-white'>{p.entity_id}</td>
                      <td className='py-1 px-2 text-gray-400'>{p.label ?? '—'}</td>
                      <td className='py-1 px-2 font-mono text-gray-400'>
                        {fmtCoord(p.lat)}, {fmtCoord(p.lon)}
                      </td>
                      <td className='py-1 px-2 text-gray-500'>{p.source}</td>
                      <td className='py-1 px-2 text-right'>
                        <Button
                          variant='danger'
                          size='sm'
                          icon={<Trash2 size={10} />}
                          loading={delMut.isPending}
                          onClick={async () => {
                            if (await themedConfirm(t('geo.confirmDeletePos', { id: p.entity_id }))) {
                              delMut.mutate(p);
                            }
                          }}
                        >
                          {t('geo.delete')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </CardContent>

      {adding && (
        <Modal onClose={() => setAdding(false)} title={t('geo.addPosition')} size='sm'>
          <div className='space-y-3'>
            <div>
              <label className='text-xs text-gray-500'>{t('geo.colKind')}</label>
              <Select
                value={form.kind}
                onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))}
              >
                {['node', 'operator', 'device', 'vehicle', 'asset', 'traccar_device', 'manual'].map(
                  (k) => <option key={k} value={k}>{k}</option>,
                )}
              </Select>
            </div>
            <div>
              <label className='text-xs text-gray-500'>{t('geo.colEntity')}</label>
              <input
                className='w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm font-mono'
                value={form.entity_id}
                onChange={(e) => setForm((f) => ({ ...f, entity_id: e.target.value }))}
                placeholder='acm1 / user-id / device-uniqueId'
              />
            </div>
            <div>
              <label className='text-xs text-gray-500'>{t('geo.colLabel')}</label>
              <input
                className='w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm'
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              />
            </div>
            <div className='grid grid-cols-2 gap-2'>
              <div>
                <label className='text-xs text-gray-500'>Lat</label>
                <input
                  className='w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm font-mono'
                  value={form.lat}
                  onChange={(e) => setForm((f) => ({ ...f, lat: e.target.value }))}
                  placeholder='50.0755'
                />
              </div>
              <div>
                <label className='text-xs text-gray-500'>Lon</label>
                <input
                  className='w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm font-mono'
                  value={form.lon}
                  onChange={(e) => setForm((f) => ({ ...f, lon: e.target.value }))}
                  placeholder='14.4378'
                />
              </div>
            </div>
            <div className='flex gap-2 justify-end'>
              <Button variant='secondary' onClick={() => setAdding(false)}>{t('common.cancel')}</Button>
              <Button
                variant='primary'
                loading={addMut.isPending}
                disabled={!form.entity_id.trim() || !form.lat || !form.lon}
                onClick={() => addMut.mutate()}
              >
                {t('geo.save')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </Card>
  );
}

// ── Callers panel ────────────────────────────────────────────────────────────

function CallersPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [scope, setScope] = useState<'all' | 'private' | 'public'>('all');
  const [q, setQ] = useState('');
  const [assignIp, setAssignIp] = useState<string | null>(null);
  const [assignLabel, setAssignLabel] = useState('');
  const [assignUser, setAssignUser] = useState('');

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['geo-callers', scope, q],
    queryFn: () =>
      callMcp<{ ok: boolean; callers: GeoCaller[]; total: number }>('geo.caller_list', {
        scope,
        q: q.trim() || undefined,
        limit: 300,
      }),
    refetchInterval: 20_000,
  });
  const callers = data?.callers ?? [];

  const geocodeMut = useMutation({
    mutationFn: async () => {
      const r = await callMcp<{ ok?: boolean; processed?: number; error?: string }>(
        'geo.caller_geocode',
        { action: 'pending', limit: 10 },
      );
      if (r && r.ok === false) throw new Error(r.error || 'geocode failed');
      return r;
    },
    onSuccess: (r: { processed?: number }) => {
      toast.success(t('geo.geocodeDone', { n: r.processed ?? 0 }));
      qc.invalidateQueries({ queryKey: ['geo-callers'] });
      qc.invalidateQueries({ queryKey: ['geo-map'] });
    },
    onError: (e: Error) => toast.error(e.message || String(e)),
  });

  const geocodeOneMut = useMutation({
    mutationFn: async (ip: string) => {
      const r = await callMcp<{ ok?: boolean; skipped?: boolean; reason?: string; error?: string }>(
        'geo.caller_geocode',
        { action: 'one', ip },
      );
      if (r && r.ok === false) throw new Error(r.error || 'geocode failed');
      if (r?.skipped) throw new Error(r.reason || 'skipped');
      return r;
    },
    onSuccess: () => {
      toast.success(t('geo.geocodeOneDone'));
      qc.invalidateQueries({ queryKey: ['geo-callers'] });
      qc.invalidateQueries({ queryKey: ['geo-map'] });
    },
    onError: (e: Error) => toast.error(e.message || String(e)),
  });

  const assignMut = useMutation({
    mutationFn: () =>
      callMcp('geo.caller_assign', {
        ip: assignIp!,
        assigned_label: assignLabel.trim() || undefined,
        assigned_user_id: assignUser.trim() || null,
      }),
    onSuccess: () => {
      toast.success(t('geo.assigned'));
      setAssignIp(null);
      setAssignLabel('');
      setAssignUser('');
      qc.invalidateQueries({ queryKey: ['geo-callers'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const blockMut = useMutation({
    mutationFn: (ip: string) =>
      callMcp('self-defence.ip_list_add', {
        list_type: 'blacklist',
        ip,
        reason: 'From geo caller list',
        source: 'manual',
      }),
    onSuccess: () => toast.success(t('geo.blocked')),
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card>
      <CardHeader>
        <div className='flex items-center justify-between w-full flex-wrap gap-2'>
          <CardTitle icon={Wifi}>
            {t('geo.callersTitle')} ({data?.total ?? 0})
          </CardTitle>
          <div className='flex gap-2 items-center flex-wrap'>
            <Select value={scope} onChange={(e) => setScope(e.target.value as typeof scope)}>
              <option value='all'>{t('geo.scopeAll')}</option>
              <option value='public'>{t('geo.scopePublic')}</option>
              <option value='private'>{t('geo.scopePrivate')}</option>
            </Select>
            <input
              className='bg-gray-900 border border-gray-700 rounded px-2 py-1 text-xs w-40'
              placeholder={t('geo.searchPlaceholder')}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            <Button variant='secondary' size='sm' icon={<RefreshCw size={12} />} onClick={() => refetch()}>
              {t('geo.refresh')}
            </Button>
            <Button
              variant='primary'
              size='sm'
              icon={<Globe size={12} />}
              loading={geocodeMut.isPending}
              onClick={() => geocodeMut.mutate()}
            >
              {t('geo.geocodePending')}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading
          ? <div className='text-xs text-gray-600 py-4'>{t('common.loading')}</div>
          : callers.length === 0
          ? <div className='text-xs text-gray-600 py-4 text-center'>{t('geo.noCallers')}</div>
          : (
            <div className='overflow-x-auto max-h-[28rem] overflow-y-auto'>
              <table className='w-full text-xs'>
                <thead className='sticky top-0 bg-black'>
                  <tr className='text-gray-500 border-b border-gray-800'>
                    <th className='text-left py-1 px-2'>IP</th>
                    <th className='text-left py-1 px-2'>{t('geo.colScope')}</th>
                    <th className='text-left py-1 px-2'>{t('geo.colCustomer')}</th>
                    <th className='text-left py-1 px-2'>{t('geo.colCountry')}</th>
                    <th className='text-left py-1 px-2'>{t('geo.colCity')}</th>
                    <th className='text-left py-1 px-2'>{t('geo.colHits')}</th>
                    <th className='text-left py-1 px-2'>{t('geo.colUser')}</th>
                    <th className='text-left py-1 px-2'>{t('geo.colLastSeen')}</th>
                    <th className='text-right py-1 px-2'>{t('geo.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {callers.map((c) => (
                    <tr key={c.id} className='border-b border-gray-900 hover:bg-gray-900/40'>
                      <td className='py-1 px-2 font-mono text-white'>{c.ip}</td>
                      <td className='py-1 px-2'>
                        <Badge variant={c.is_private ? 'default' : 'info'}>
                          {c.is_private ? t('geo.scopePrivate') : t('geo.scopePublic')}
                        </Badge>
                        {c.country_policy === 'block' && (
                          <Badge variant='error' className='ml-1'>block</Badge>
                        )}
                      </td>
                      <td className='py-1 px-2 text-gray-400'>
                        {c.customer_label || c.customer_id || c.network_cidr || '—'}
                      </td>
                      <td className='py-1 px-2 text-gray-400'>
                        {c.country_code ?? '—'}
                        {c.country_name ? ` · ${c.country_name}` : ''}
                      </td>
                      <td className='py-1 px-2 text-gray-400'>{c.city ?? '—'}</td>
                      <td className='py-1 px-2 font-mono text-gray-400'>{c.hit_count}</td>
                      <td className='py-1 px-2 text-gray-400'>
                        {c.assigned_label || c.last_username || c.assigned_user_id || '—'}
                      </td>
                      <td className='py-1 px-2 text-gray-500'>
                        {new Date(c.last_seen).toLocaleString()}
                      </td>
                      <td className='py-1 px-2 text-right'>
                        <div className='flex justify-end gap-1'>
                          {!c.is_private && !c.lat && (
                            <Button
                              variant='secondary'
                              size='sm'
                              loading={geocodeOneMut.isPending}
                              onClick={() => geocodeOneMut.mutate(c.ip)}
                            >
                              Geo
                            </Button>
                          )}
                          <Button
                            variant='secondary'
                            size='sm'
                            icon={<User size={10} />}
                            onClick={async () => {
                              setAssignIp(c.ip);
                              setAssignLabel(c.assigned_label ?? '');
                              setAssignUser(c.assigned_user_id ?? '');
                            }}
                          >
                            {t('geo.assign')}
                          </Button>
                          {!c.is_private && (
                            <Button
                              variant='danger'
                              size='sm'
                              icon={<Ban size={10} />}
                              loading={blockMut.isPending}
                              onClick={async () => {
                                if (await themedConfirm(t('geo.confirmBlock', { ip: c.ip }))) {
                                  blockMut.mutate(c.ip);
                                }
                              }}
                            >
                              BL
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </CardContent>

      {assignIp && (
        <Modal onClose={() => setAssignIp(null)} title={t('geo.assignTitle', { ip: assignIp })} size='sm'>
          <div className='space-y-3'>
            <div>
              <label className='text-xs text-gray-500'>{t('geo.assignLabel')}</label>
              <input
                className='w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm'
                value={assignLabel}
                onChange={(e) => setAssignLabel(e.target.value)}
                placeholder='Filip notebook / office VPN'
              />
            </div>
            <div>
              <label className='text-xs text-gray-500'>{t('geo.assignUserId')}</label>
              <input
                className='w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm font-mono'
                value={assignUser}
                onChange={(e) => setAssignUser(e.target.value)}
                placeholder='entity uuid (optional)'
              />
            </div>
            <div className='flex gap-2 justify-end'>
              <Button variant='secondary' onClick={() => setAssignIp(null)}>{t('common.cancel')}</Button>
              <Button variant='primary' loading={assignMut.isPending} onClick={() => assignMut.mutate()}>
                {t('geo.save')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </Card>
  );
}

// ── Customer networks panel ──────────────────────────────────────────────────

function NetworksPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [scope, setScope] = useState<'all' | 'private' | 'public'>('all');
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({
    cidr: '',
    label: '',
    customer_id: '',
    customer_label: '',
    kind: 'customer',
    color: '#6366f1',
    lat: '',
    lon: '',
    notes: '',
  });

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['geo-networks', scope],
    queryFn: () =>
      callMcp<{ ok: boolean; networks: GeoNetwork[]; private: number; public: number }>(
        'geo.network_list',
        { scope },
      ),
    refetchInterval: 30_000,
  });
  const networks = data?.networks ?? [];

  const setMut = useMutation({
    mutationFn: () =>
      callMcp('geo.network_set', {
        cidr: form.cidr.trim(),
        label: form.label.trim() || undefined,
        customer_id: form.customer_id.trim() || undefined,
        customer_label: form.customer_label.trim() || undefined,
        kind: form.kind,
        color: form.color || undefined,
        lat: form.lat ? parseFloat(form.lat) : undefined,
        lon: form.lon ? parseFloat(form.lon) : undefined,
        notes: form.notes.trim() || undefined,
      }),
    onSuccess: () => {
      toast.success(t('geo.networkSaved'));
      setAdding(false);
      setForm({
        cidr: '',
        label: '',
        customer_id: '',
        customer_label: '',
        kind: 'customer',
        color: '#6366f1',
        lat: '',
        lon: '',
        notes: '',
      });
      qc.invalidateQueries({ queryKey: ['geo-networks'] });
      qc.invalidateQueries({ queryKey: ['geo-map'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => callMcp('geo.network_delete', { id }),
    onSuccess: () => {
      toast.success(t('geo.networkDeleted'));
      qc.invalidateQueries({ queryKey: ['geo-networks'] });
      qc.invalidateQueries({ queryKey: ['geo-map'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card>
      <CardHeader>
        <div className='flex items-center justify-between w-full flex-wrap gap-2'>
          <CardTitle icon={Building2}>
            {t('geo.networksTitle')} ({networks.length})
          </CardTitle>
          <div className='flex gap-2 items-center'>
            <Select value={scope} onChange={(e) => setScope(e.target.value as typeof scope)}>
              <option value='all'>{t('geo.scopeAll')}</option>
              <option value='private'>{t('geo.scopePrivate')}</option>
              <option value='public'>{t('geo.scopePublic')}</option>
            </Select>
            <Button variant='secondary' size='sm' icon={<RefreshCw size={12} />} onClick={() => refetch()}>
              {t('geo.refresh')}
            </Button>
            <Button variant='primary' size='sm' icon={<Plus size={12} />} onClick={() => setAdding(true)}>
              {t('geo.addNetwork')}
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <p className='text-xs text-gray-500 mb-3'>{t('geo.networksHint')}</p>
        {isLoading
          ? <div className='text-xs text-gray-600'>{t('common.loading')}</div>
          : networks.length === 0
          ? <div className='text-xs text-gray-600 text-center py-4'>{t('geo.noNetworks')}</div>
          : (
            <div className='overflow-x-auto max-h-96 overflow-y-auto'>
              <table className='w-full text-xs'>
                <thead className='sticky top-0 bg-black'>
                  <tr className='text-gray-500 border-b border-gray-800'>
                    <th className='text-left py-1 px-2'>CIDR</th>
                    <th className='text-left py-1 px-2'>{t('geo.colScope')}</th>
                    <th className='text-left py-1 px-2'>{t('geo.colCustomer')}</th>
                    <th className='text-left py-1 px-2'>{t('geo.colKind')}</th>
                    <th className='text-left py-1 px-2'>Map</th>
                    <th className='text-right py-1 px-2'>{t('geo.colActions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {networks.map((n) => (
                    <tr key={n.id} className='border-b border-gray-900'>
                      <td className='py-1 px-2 font-mono text-white'>
                        <span
                          className='inline-block w-2 h-2 rounded-full mr-1.5 align-middle'
                          style={{ background: n.color || '#6366f1' }}
                        />
                        {n.cidr}
                        {n.label ? <span className='text-gray-500 ml-1'>({n.label})</span> : null}
                      </td>
                      <td className='py-1 px-2'>
                        <Badge variant={n.is_private ? 'default' : 'info'}>
                          {n.is_private ? t('geo.scopePrivate') : t('geo.scopePublic')}
                        </Badge>
                      </td>
                      <td className='py-1 px-2 text-gray-400'>
                        {n.customer_label || n.customer_id || '—'}
                      </td>
                      <td className='py-1 px-2 text-gray-500'>{n.kind}</td>
                      <td className='py-1 px-2 font-mono text-gray-500'>
                        {n.lat != null && n.lon != null
                          ? `${fmtCoord(n.lat, 3)}, ${fmtCoord(n.lon, 3)}`
                          : '—'}
                      </td>
                      <td className='py-1 px-2 text-right'>
                        <Button
                          variant='danger'
                          size='sm'
                          icon={<Trash2 size={10} />}
                          loading={delMut.isPending}
                          onClick={() => delMut.mutate(n.id)}
                        >
                          {t('geo.delete')}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </CardContent>

      {adding && (
        <Modal onClose={() => setAdding(false)} title={t('geo.addNetwork')} size='md'>
          <div className='space-y-3'>
            <div>
              <label className='text-xs text-gray-500'>CIDR</label>
              <input
                className='w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm font-mono'
                value={form.cidr}
                onChange={(e) => setForm((f) => ({ ...f, cidr: e.target.value }))}
                placeholder='10.50.0.0/16'
              />
            </div>
            <div className='grid grid-cols-2 gap-2'>
              <div>
                <label className='text-xs text-gray-500'>{t('geo.colCustomer')}</label>
                <input
                  className='w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm'
                  value={form.customer_label}
                  onChange={(e) => setForm((f) => ({ ...f, customer_label: e.target.value }))}
                  placeholder='Acme s.r.o.'
                />
              </div>
              <div>
                <label className='text-xs text-gray-500'>customer_id</label>
                <input
                  className='w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm font-mono'
                  value={form.customer_id}
                  onChange={(e) => setForm((f) => ({ ...f, customer_id: e.target.value }))}
                  placeholder='tenant-uuid'
                />
              </div>
            </div>
            <div className='grid grid-cols-3 gap-2'>
              <div>
                <label className='text-xs text-gray-500'>{t('geo.colLabel')}</label>
                <input
                  className='w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm'
                  value={form.label}
                  onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                />
              </div>
              <div>
                <label className='text-xs text-gray-500'>kind</label>
                <Select
                  value={form.kind}
                  onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value }))}
                >
                  {['customer', 'office', 'vpn', 'mesh', 'isp', 'datacenter', 'other'].map((k) => (
                    <option key={k} value={k}>{k}</option>
                  ))}
                </Select>
              </div>
              <div>
                <label className='text-xs text-gray-500'>color</label>
                <input
                  type='color'
                  className='w-full h-8 bg-gray-900 border border-gray-700 rounded'
                  value={form.color}
                  onChange={(e) => setForm((f) => ({ ...f, color: e.target.value }))}
                />
              </div>
            </div>
            <div className='grid grid-cols-2 gap-2'>
              <div>
                <label className='text-xs text-gray-500'>Lat (mapa)</label>
                <input
                  className='w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm font-mono'
                  value={form.lat}
                  onChange={(e) => setForm((f) => ({ ...f, lat: e.target.value }))}
                  placeholder='50.07'
                />
              </div>
              <div>
                <label className='text-xs text-gray-500'>Lon</label>
                <input
                  className='w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-sm font-mono'
                  value={form.lon}
                  onChange={(e) => setForm((f) => ({ ...f, lon: e.target.value }))}
                  placeholder='14.44'
                />
              </div>
            </div>
            <p className='text-[10px] text-gray-600'>{t('geo.networkMapHint')}</p>
            <div className='flex gap-2 justify-end'>
              <Button variant='secondary' onClick={() => setAdding(false)}>{t('common.cancel')}</Button>
              <Button
                variant='primary'
                loading={setMut.isPending}
                disabled={!form.cidr.trim()}
                onClick={() => setMut.mutate()}
              >
                {t('geo.save')}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </Card>
  );
}

// ── Country policy panel ─────────────────────────────────────────────────────

function CountryPanel() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [listType, setListType] = useState<'block' | 'allow'>('block');
  const [reason, setReason] = useState('');
  const [meshShare, setMeshShare] = useState(true);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['geo-countries'],
    queryFn: () =>
      callMcp<{ ok: boolean; policies: CountryPolicy[] }>('geo.country_policy_list', {}),
    refetchInterval: 30_000,
  });
  const policies = data?.policies ?? [];

  const setMut = useMutation({
    mutationFn: () =>
      callMcp('geo.country_policy_set', {
        country_code: code.trim().toUpperCase(),
        list_type: listType,
        reason: reason.trim() || undefined,
        mesh_share: meshShare,
      }),
    onSuccess: () => {
      toast.success(t('geo.countrySaved'));
      setCode('');
      setReason('');
      qc.invalidateQueries({ queryKey: ['geo-countries'] });
      qc.invalidateQueries({ queryKey: ['geo-map'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const rmMut = useMutation({
    mutationFn: (cc: string) =>
      callMcp('geo.country_policy_remove', { country_code: cc, mesh_unshare: true }),
    onSuccess: () => {
      toast.success(t('geo.countryRemoved'));
      qc.invalidateQueries({ queryKey: ['geo-countries'] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <Card>
      <CardHeader>
        <div className='flex items-center justify-between w-full'>
          <CardTitle icon={Globe}>
            {t('geo.countriesTitle')} ({policies.length})
          </CardTitle>
          <Button variant='secondary' size='sm' icon={<RefreshCw size={12} />} onClick={() => refetch()}>
            {t('geo.refresh')}
          </Button>
        </div>
      </CardHeader>
      <CardContent className='space-y-4'>
        <p className='text-xs text-gray-500'>{t('geo.countriesHint')}</p>
        <div className='grid grid-cols-1 sm:grid-cols-12 gap-3 items-end'>
          <div className='sm:col-span-2'>
            <Label htmlFor='geo-cc-iso'>ISO</Label>
            <Input
              id='geo-cc-iso'
              size='sm'
              className='font-mono uppercase text-center tracking-widest'
              maxLength={2}
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder='RU'
              autoComplete='off'
            />
          </div>
          <div className='sm:col-span-3'>
            <Label htmlFor='geo-cc-policy'>{t('geo.colListType')}</Label>
            <Select
              id='geo-cc-policy'
              size='sm'
              className='w-full'
              value={listType}
              onChange={(e) => setListType(e.target.value as 'block' | 'allow')}
              options={[
                { value: 'block', label: 'block' },
                { value: 'allow', label: 'allow' },
              ]}
            />
          </div>
          <div className='sm:col-span-4'>
            <Label htmlFor='geo-cc-reason'>{t('geo.colReason')}</Label>
            <Input
              id='geo-cc-reason'
              size='sm'
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('geo.colReason')}
            />
          </div>
          <div className='sm:col-span-2 flex items-center min-h-8 pb-0.5'>
            <Toggle
              value={meshShare}
              onSave={(v) => setMeshShare(v)}
              label='mesh'
            />
          </div>
          <div className='sm:col-span-1'>
            <Button
              variant='primary'
              size='sm'
              className='w-full h-8'
              loading={setMut.isPending}
              disabled={!/^[A-Za-z]{2}$/.test(code.trim())}
              onClick={() => setMut.mutate()}
            >
              {t('geo.save')}
            </Button>
          </div>
        </div>

        {isLoading
          ? <div className='text-xs text-gray-600'>{t('common.loading')}</div>
          : policies.length === 0
          ? <div className='text-xs text-gray-600 text-center py-4'>{t('geo.noCountries')}</div>
          : (
            <table className='w-full text-xs'>
              <thead>
                <tr className='text-gray-500 border-b border-gray-800'>
                  <th className='text-left py-1 px-2'>CC</th>
                  <th className='text-left py-1 px-2'>{t('geo.colListType')}</th>
                  <th className='text-left py-1 px-2'>{t('geo.colReason')}</th>
                  <th className='text-right py-1 px-2'>{t('geo.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {policies.map((p) => (
                  <tr key={p.country_code} className='border-b border-gray-900'>
                    <td className='py-1 px-2 font-mono text-white'>{p.country_code}</td>
                    <td className='py-1 px-2'>
                      <Badge variant={p.list_type === 'block' ? 'error' : 'info'}>{p.list_type}</Badge>
                    </td>
                    <td className='py-1 px-2 text-gray-400'>{p.reason ?? '—'}</td>
                    <td className='py-1 px-2 text-right'>
                      <Button
                        variant='danger'
                        size='sm'
                        icon={<Trash2 size={10} />}
                        loading={rmMut.isPending}
                        onClick={() => rmMut.mutate(p.country_code)}
                      >
                        {t('geo.delete')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
      </CardContent>
    </Card>
  );
}

// ── Operator heartbeat (browser geolocation) — best-effort, never throws ─────

function useOperatorHeartbeat() {
  useEffect(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) return;
    let cancelled = false;
    const send = () => {
      try {
        navigator.geolocation.getCurrentPosition(
          (pos) => {
            if (cancelled) return;
            const lat = pos.coords.latitude;
            const lon = pos.coords.longitude;
            if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
            callMcp('geo.operator_heartbeat', {
              lat,
              lon,
              accuracy: pos.coords.accuracy,
            }).catch(() => {});
          },
          () => {},
          { enableHighAccuracy: false, maximumAge: 300_000, timeout: 10_000 },
        );
      } catch {
        /* geolocation API unavailable */
      }
    };
    send();
    const id = setInterval(send, 15 * 60_000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, []);
}

// ── Main page ────────────────────────────────────────────────────────────────

export default function GeoPage() {
  const { t } = useTranslation();
  useOperatorHeartbeat();

  const { data: snap, refetch, isLoading } = useQuery({
    queryKey: ['geo-map-stats'],
    queryFn: () =>
      callMcp<{
        ok: boolean;
        summary: {
          positions: number;
          by_kind?: Record<string, number>;
          callers_with_coords?: number;
          callers_mapped?: number;
          callers?: number;
          country_policies: number;
          networks?: number;
          customers?: number;
        };
      }>('geo.map_snapshot', { include_local_networks: false }),
    refetchInterval: 30_000,
  });

  const publishAllMut = useMutation({
    mutationFn: () => callMcp('self-defence.ip_list_publish_all', {}),
    onSuccess: (r: { published?: number }) => {
      toast.success(t('geo.meshPublishDone', { n: r.published ?? 0 }));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const byKind = snap?.summary.by_kind ?? {};
  const callersMapped = snap?.summary.callers_with_coords ??
    snap?.summary.callers_mapped ??
    snap?.summary.callers ??
    0;

  return (
    <div className='space-y-4'>
      <StatsGrid columns={4}>
        <StatCard
          label={t('geo.statPositions')}
          value={snap?.summary.positions ?? (isLoading ? '…' : 0)}
          icon={MapPin}
        />
        <StatCard
          label={t('geo.statCallersMapped')}
          value={callersMapped}
          icon={Globe}
          variant='info'
        />
        <StatCard
          label={t('geo.statNodes')}
          value={byKind.node ?? 0}
          icon={Server}
        />
        <StatCard
          label={t('geo.statNetworks')}
          value={snap?.summary.networks ?? 0}
          icon={Network}
          variant={(snap?.summary.networks ?? 0) > 0 ? 'info' : 'default'}
        />
      </StatsGrid>

      <div className='flex flex-wrap gap-2'>
        <Button variant='secondary' size='sm' icon={<RefreshCw size={12} />} onClick={() => refetch()}>
          {t('geo.refresh')}
        </Button>
        <Button
          variant='secondary'
          size='sm'
          icon={<Navigation size={12} />}
          loading={publishAllMut.isPending}
          onClick={() => publishAllMut.mutate()}
          title={t('geo.meshPublishHint')}
        >
          {t('geo.meshPublishLists')}
        </Button>
        <span className='text-[10px] text-gray-600 self-center'>
          Widget <code className='text-gray-400'>geo.map</code> · Traccar-ready
        </span>
      </div>

      <Tabs defaultTab='map'>
        <TabList>
          <Tab id='map' icon={<MapIcon size={14} />}>{t('geo.tabMap')}</Tab>
          <Tab id='positions' icon={<Crosshair size={14} />}>{t('geo.tabPositions')}</Tab>
          <Tab id='callers' icon={<Wifi size={14} />}>{t('geo.tabCallers')}</Tab>
          <Tab id='networks' icon={<Building2 size={14} />}>{t('geo.tabNetworks')}</Tab>
          <Tab id='countries' icon={<Globe size={14} />}>{t('geo.tabCountries')}</Tab>
        </TabList>
        <TabPanel id='map'>
          <MapPanel />
        </TabPanel>
        <TabPanel id='positions'>
          <PositionsPanel />
        </TabPanel>
        <TabPanel id='callers'>
          <CallersPanel />
        </TabPanel>
        <TabPanel id='networks'>
          <NetworksPanel />
        </TabPanel>
        <TabPanel id='countries'>
          <CountryPanel />
        </TabPanel>
      </Tabs>
    </div>
  );
}
